import confetti from 'canvas-confetti';
import { useCallback } from 'react';

// Brand colors for confetti
const BRAND_COLORS = [
  '#22D3EE', // Primary cyan
  '#3ee0f5', // Light cyan
  '#0ea5e9', // Sky blue
  '#06b6d4', // Cyan 500
  '#67e8f9', // Cyan 300
  '#a5f3fc', // Cyan 200
  '#f0f0f0', // Off-white
];

interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  origin?: { x: number; y: number };
  startVelocity?: number;
  gravity?: number;
  ticks?: number;
  scalar?: number;
}

export function useConfetti() {
  const fireConfetti = useCallback((options: ConfettiOptions = {}) => {
    const defaults: ConfettiOptions = {
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 0.5 }, // Center of screen
      startVelocity: 45,
      gravity: 1.2,
      ticks: 200,
      scalar: 1.2,
    };

    const mergedOptions = { ...defaults, ...options };

    // Fire confetti burst
    confetti({
      ...mergedOptions,
      colors: BRAND_COLORS,
      shapes: ['square', 'circle'],
      disableForReducedMotion: true,
    });
  }, []);

  const fireCelebration = useCallback(() => {
    // Fire multiple bursts for a more dramatic effect
    const count = 200;
    const defaults = {
      origin: { y: 0.5 },
      colors: BRAND_COLORS,
      disableForReducedMotion: true,
    };

    // Left burst
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      startVelocity: 45,
      gravity: 1.2,
      ticks: 200,
    });

    // Right burst
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      startVelocity: 45,
      gravity: 1.2,
      ticks: 200,
    });

    // Center burst (delayed slightly)
    setTimeout(() => {
      confetti({
        ...defaults,
        particleCount: count / 3,
        spread: 100,
        origin: { x: 0.5, y: 0.5 },
        startVelocity: 50,
        gravity: 1,
        ticks: 250,
        scalar: 1.3,
      });
    }, 100);
  }, []);

  return { fireConfetti, fireCelebration };
}

// Standalone function for use outside of React components
export function triggerCelebration() {
  const count = 200;
  const colors = BRAND_COLORS;
  const defaults = {
    colors,
    disableForReducedMotion: true,
  };

  // Left burst
  confetti({
    ...defaults,
    particleCount: count / 2,
    angle: 60,
    spread: 55,
    origin: { x: 0, y: 0.6 },
    startVelocity: 45,
    gravity: 1.2,
    ticks: 200,
  });

  // Right burst
  confetti({
    ...defaults,
    particleCount: count / 2,
    angle: 120,
    spread: 55,
    origin: { x: 1, y: 0.6 },
    startVelocity: 45,
    gravity: 1.2,
    ticks: 200,
  });

  // Center burst (delayed)
  setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: count / 3,
      spread: 100,
      origin: { x: 0.5, y: 0.5 },
      startVelocity: 50,
      gravity: 1,
      ticks: 250,
      scalar: 1.3,
    });
  }, 100);
}
